import { FeatureCollection, GeoJsonProperties, Geometry, Polygon } from 'geojson';

import {
    BaseQueryApi,
    createApi,
    fetchBaseQuery,
    FetchBaseQueryArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta
} from '@reduxjs/toolkit/query/react';
import { booleanWithin, buffer } from '@turf/turf';

import { EMISSION_DB_URL, IN_IAP, INFRASTRUCTURE_API_BASE_URL, TARGETS_URL } from '../../environmentVariables';
import {
    CaptureFeatureProperties,
    CombinedOGIMProperties,
    HistogramData,
    LoadedData,
    RasterPointFeature,
    STACCollection,
    StacFeature,
    StacFilterLang,
    StacSearch,
    StacSearchResponse,
    TargetData,
    TiTilerResponse
} from '../../types';
import { Notifier } from '../../utils/Notifier';
import {
    formatCollectionsForGET,
    formatFieldsForGET,
    formatFilterForGET,
    formatRasterPointFeature,
    formatSortbyForGET,
    isSTACCollections,
    isSTACFields,
    isSTACFilter,
    isSTACSortby,
    normalizeTargetIds,
    StacSearchResponseBuilder
} from '../../utils/stac';
import { OGI_NEAR_PLUME_RADIUS_M } from '../../consts';

/**
 * Type predicates to narrow an unknown error to `FetchBaseQueryError` or SerializedError
 * https://redux-toolkit.js.org/rtk-query/usage-with-typescript#inline-error-handling-example
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === 'object' && error != null && 'status' in error;
}

export function isErrorWithMessage(error: unknown): error is { message: string } {
    return (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof error === 'object' && error != null && 'message' in error && typeof (error as any).message === 'string'
    );
}

/**
 * Formats STAC search parameters for GET or POST.
 * Iterates through each stacSearch key and adds the value to the GET URL search params
 */
function addStacSearchParamsToUrl(url: URL, stacSearch: StacSearch) {
    Object.entries(stacSearch).forEach(([key, val]) => {
        // Some complex objects require special transforms to pass them into a URL for
        // STAC search GET.
        if (key === 'collections' && isSTACCollections(val)) {
            url.searchParams.set('collections', formatCollectionsForGET(val));
        } else if (key === 'filter' && isSTACFilter(val)) {
            url.searchParams.set('filter', formatFilterForGET(val));
            // Since a filter is set and we're using GET, set filter-lang to CQL2 text.
            url.searchParams.set('filter-lang', StacFilterLang.cql2Text);
        } else if (key === 'sortby' && isSTACSortby(val)) {
            url.searchParams.set('sortby', formatSortbyForGET(val));
        } else if (key === 'fields' && isSTACFields(val)) {
            url.searchParams.set('fields', formatFieldsForGET(val));
        } else {
            // Fallthrough: val is already a primitive that doesn't need a transform.
            url.searchParams.set(key, `${val}`);
        }
    });
    return url;
}

interface StacSearchResponseBaseQuery<G extends Geometry = Geometry, P = GeoJsonProperties> {
    error?: FetchBaseQueryError;
    data?: StacSearchResponse<G, P>;
    meta?: FetchBaseQueryMeta;
}

/** Custom base query to add IAP headers, necessary for dev & staging deployments */
const fetchCustomBaseQuery = (arg: FetchBaseQueryArgs) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async (args: any, api: BaseQueryApi, extraOptions: any) => {
        const defaultBaseQuery = fetchBaseQuery({
            ...arg,
            prepareHeaders: (headers: Headers) => {
                if (IN_IAP) {
                    headers.set('X-Requested-With', 'XMLHttpRequest');
                }
                return headers;
            }
        });
        try {
            const result = await defaultBaseQuery(args, api, extraOptions);
            if (IN_IAP && result.error && result.error.status === 401) {
                new Notifier().notify();
            }
            return result;
        } catch (error) {
            if (isFetchBaseQueryError(error)) {
                const errMsg = 'error' in error ? error.error : error.data;
                return { error: { status: error.status, data: errMsg } };
            } else if (isErrorWithMessage(error)) {
                return { error: { status: 400, data: error.message } };
            } else {
                return { error: { status: 400, data: 'An unknown error occurred' } };
            }
        }
    };
};
/** Custom STAC-specific base query that handles normalizing target IDs to strings. */
const fetchStacBaseQuery = (arg: FetchBaseQueryArgs) => {
    const customBaseQuery = fetchCustomBaseQuery(arg);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async (args: any, api: BaseQueryApi, extraOptions: any) => {
        const result = await customBaseQuery(args, api, extraOptions);
        if ('data' in result) {
            const data = result.data as StacSearchResponse | StacFeature;
            if ('features' in data) normalizeTargetIds(data.features);
            else if (data.type === 'Feature') normalizeTargetIds([data as StacFeature]);
        }
        return result;
    };
};

/**
Testing notes: 

* Any new endpoints MUST be added to makeApiSlice in packages/app/test/unit/index.tsx
Omitting endpoints there will cause tests for the component that uses them to fail 
(generally, they'll fail to run at all).

* Any new api slices must be added to the store in wrappedRender in 
packages/app/test/unit/index.tsx (add to both the middleware chain and the list of reducers).

*/
export const stacAPISlice = createApi({
    reducerPath: 'stac',
    baseQuery: fetchStacBaseQuery({
        baseUrl: EMISSION_DB_URL
    }),
    endpoints: (builder) => ({
        // transform: most recent + captures with l4 cogs
        getStacSearch: builder.query<
            StacSearchResponse<Polygon, CaptureFeatureProperties>,
            { stacSearch: StacSearch | null; filterToLatest?: boolean; filterToResultsWithMatchingTotals?: boolean }
        >({
            /**
             * Note: for STAC search prefer using GET to take advantage of the CDN.  Otherwise using
             * POST with no CDN greatly limits our max concurrent users.  For simple search queries
             * it's recommended to use GET, especially for primary features that are called by every
             * user of Portal.
             *
             * In the future for secondary features not used by every user, it might be worth
             * considering adding a separate POST query for more full-featured filtering.
             *
             * See https://api.stacspec.org/v1.0.0-beta.3/item-search/#tag/Item-Search/operation/getItemSearch
             */
            query: ({
                stacSearch,
                filterToLatest: _filterToLatest = true,
                filterToResultsWithMatchingTotals: _filterToResultsWithMatchingTotals = true
            }) => {
                if (stacSearch === null) {
                    return;
                }

                const url = addStacSearchParamsToUrl(
                    new URL(`${EMISSION_DB_URL}/search`, window.location.origin),
                    stacSearch
                );

                return {
                    url: `${url}`,
                    method: 'GET'
                };
            },
            transformResponse: (
                res: StacSearchResponse<Polygon, CaptureFeatureProperties>,
                _meta,
                { filterToLatest = true, filterToResultsWithMatchingTotals = true }
            ) =>
                new StacSearchResponseBuilder(res)
                    .filterToLatestProcessingIdPerCollection({ skip: !filterToLatest })
                    .filterToCapturesAndPointsWithMatchingTotals({ skip: !filterToResultsWithMatchingTotals })
                    .withTargetGroupData()
                    .data()
        }),
        getStacSearchByUrl: builder.query<
            StacSearchResponse<Polygon, CaptureFeatureProperties>,
            { searchParams: string; filterToLatest?: boolean; filterToResultsWithMatchingTotals?: boolean }
        >({
            query: ({
                searchParams,
                filterToLatest: _filterToLatest = true,
                filterToResultsWithMatchingTotals: _filterToResultsWithMatchingTotals = true
            }) => {
                const url = new URL(`${EMISSION_DB_URL}/search?${searchParams}`, window.location.origin);
                return {
                    url: `${url}`,
                    method: 'GET'
                };
            },
            transformResponse: (
                res: StacSearchResponse<Polygon, CaptureFeatureProperties>,
                _meta,
                { filterToLatest }
            ) =>
                new StacSearchResponseBuilder(res)
                    .filterToLatestProcessingIdPerCollection({ skip: !filterToLatest })
                    .data()
        }),
        getAllStacItemsForCollection: builder.query<
            FeatureCollection<Polygon, CaptureFeatureProperties> | undefined,
            { collectionId: string; filterToLatest?: boolean; filterToResultsWithMatchingTotals?: boolean }
        >({
            query: ({
                collectionId,
                filterToLatest: _filterToLatest = true,
                filterToResultsWithMatchingTotals: _filterToResultsWithMatchingTotals = true
            }) => {
                return { url: `/collections/${collectionId}/items?limit=1000` };
            },
            transformResponse: (
                response: StacSearchResponse<Polygon, CaptureFeatureProperties>,
                _meta,
                { filterToLatest, filterToResultsWithMatchingTotals }
            ) => {
                const updatedResponse = new StacSearchResponseBuilder(response)
                    .filterToLatestProcessingIdPerCollection({ skip: !filterToLatest })
                    .filterToCapturesAndPointsWithMatchingTotals({ skip: !filterToResultsWithMatchingTotals })
                    .withTargetGroupData()
                    .data() as FeatureCollection<Polygon, CaptureFeatureProperties>;

                return buffer(updatedResponse, 1, { units: 'kilometers' }) as FeatureCollection<
                    Polygon,
                    CaptureFeatureProperties
                >;
            }
        }),
        getStacItem: builder.query<StacFeature, { collectionId: string; itemId: string }>({
            query: ({ collectionId, itemId }) => {
                return {
                    url: `/collections/${collectionId}/items/${itemId}`
                };
            }
        }),
        getHistogramData: builder.query<
            HistogramData,
            { collection: string; selectedStacItemId: string; bins: string }
        >({
            async queryFn({ collection, selectedStacItemId, bins }, _api, _extraOptions, fetchWithBQ) {
                const itemQuery = await fetchWithBQ(
                    `/raster/collections/${collection}/items/${selectedStacItemId}/asset_statistics?assets=COG&asset_bidx=COG|1&histogram_bins=${bins}`
                );

                if (itemQuery.error) return { error: itemQuery.error as unknown as FetchBaseQueryError };

                const itemData = itemQuery.data as HistogramData;

                return { data: itemData };
            }
        }),
        // no transform
        getRasterPointFeature: builder.query<
            RasterPointFeature,
            {
                coordinates: number[];
                itemId: string;
                collectionId: STACCollection;
            }
        >({
            async queryFn({ coordinates, itemId, collectionId }, _api, _extraOptions, fetchWithBQ) {
                const [lng, lat] = coordinates;

                const itemQuery = await fetchWithBQ(`/collections/${collectionId}/items/${itemId}`);
                if (itemQuery.error) return { error: itemQuery.error as unknown as FetchBaseQueryError };
                const itemData = itemQuery.data as StacFeature;

                // Check that the point is actually inside the STAC feature
                const clickedPointGeometry: Geometry = { coordinates: [lng, lat], type: 'Point' };
                if (!booleanWithin(clickedPointGeometry, itemData)) {
                    return {
                        error: {
                            status: 400,
                            data: 'Point not in dataset bounds'
                        }
                    };
                }

                const rasterQuery = await fetchWithBQ(
                    `raster/collections/${collectionId}/items/${itemId}/point/${lng},${lat}?assets=COG`
                );
                if (rasterQuery.error) return { error: rasterQuery.error as unknown as FetchBaseQueryError };
                const cogData = rasterQuery.data as TiTilerResponse;

                return { data: formatRasterPointFeature(itemData, cogData, coordinates) };
            }
        }),
        // no transform
        getRasterPointFeatureBySearch: builder.query<
            RasterPointFeature,
            { coordinates: number[]; stacSearch: StacSearch }
        >({
            async queryFn({ coordinates, stacSearch }, _api, _extraOptions, fetchWithBQ) {
                const [lng, lat] = coordinates;

                const url = addStacSearchParamsToUrl(
                    new URL(`${EMISSION_DB_URL}/search`, window.location.origin),
                    stacSearch
                );
                const searchResponse = (await fetchWithBQ(`${url}`)) as StacSearchResponseBaseQuery;
                if (searchResponse.error || !searchResponse.data)
                    return { error: searchResponse.error as unknown as FetchBaseQueryError };
                // filtered down to latest captures
                const filteredCaptures = new StacSearchResponseBuilder(
                    searchResponse.data as StacSearchResponse<Geometry, CaptureFeatureProperties>
                )
                    .filterToLatestProcessingIdPerCollection()
                    .filterToCapturesAndPointsWithMatchingTotals()
                    .data();
                if (!filteredCaptures || !filteredCaptures.features)
                    return { error: { status: 404 } as unknown as FetchBaseQueryError };
                if (filteredCaptures && filteredCaptures.features.length !== 1) {
                    return {
                        error: {
                            status: filteredCaptures.features?.length > 1 ? 400 : 404,
                            data: filteredCaptures.features?.length > 1 ? 'TOO_MANY_RESULTS' : 'NOT FOUND'
                        } as unknown as FetchBaseQueryError
                    };
                }

                const itemData = filteredCaptures.features[0];
                const { id: itemId, collection: collectionId } = itemData;

                // Check that the point is actually inside the STAC feature
                const clickedPointGeometry: Geometry = { coordinates: [lng, lat], type: 'Point' };
                if (!booleanWithin(clickedPointGeometry, itemData)) {
                    return {
                        error: {
                            status: 400,
                            data: 'Point not in dataset bounds'
                        }
                    };
                }

                const rasterQuery = await fetchWithBQ(
                    `raster/collections/${collectionId}/items/${itemId}/point/${lng},${lat}?assets=COG`
                );
                if (rasterQuery.error) return { error: rasterQuery.error as unknown as FetchBaseQueryError };
                const cogData = rasterQuery.data as TiTilerResponse;

                return { data: formatRasterPointFeature(itemData, cogData, coordinates) };
            }
        }),
        // most recent
        getMultipleStacSearchesByUrl: builder.query<
            StacSearchResponse<Polygon, CaptureFeatureProperties>[],
            {
                searchParamsList: string[];
                filterToLatest?: boolean;
                filterToResultsWithMatchingTotals?: boolean;
            }
        >({
            queryFn: async (
                { searchParamsList, filterToLatest = true, filterToResultsWithMatchingTotals = true },
                _api,
                _extraOptions,
                fetchWithBQ
            ) => {
                const responseList = await Promise.all(
                    searchParamsList.map((searchParams) => {
                        const url = new URL(`${EMISSION_DB_URL}/search?${searchParams}`, window.location.origin);
                        return fetchWithBQ(`${url}`);
                    })
                );

                return {
                    data: responseList.map((res) => {
                        if (res.error) return { error: res.error };
                        const builder = new StacSearchResponseBuilder(
                            res.data as StacSearchResponse<Polygon, CaptureFeatureProperties>
                        );
                        return builder
                            .filterToLatestProcessingIdPerCollection({ skip: !filterToLatest })
                            .filterToCapturesAndPointsWithMatchingTotals({ skip: !filterToResultsWithMatchingTotals })
                            .withTargetGroupData()
                            .data() as StacSearchResponse<Polygon, CaptureFeatureProperties>;
                    }) as StacSearchResponse<Polygon, CaptureFeatureProperties>[]
                };
            }
        }),
        // no transform
        getMultipleRasterPointsByStacSearchUrls: builder.query<
            { data?: RasterPointFeature }[],
            {
                url: string;
                itemData: StacFeature<Polygon, CaptureFeatureProperties>;
                coordinates: number[];
            }[]
        >({
            queryFn: async (rasterPointUrlDataList, _api, _extraOptions, fetchWithBQ) => {
                const responses = await Promise.all(
                    rasterPointUrlDataList.map(({ url, itemData, coordinates }) => {
                        const clickedPointGeometry: Geometry = { coordinates, type: 'Point' };
                        if (!booleanWithin(clickedPointGeometry, itemData)) {
                            return {
                                error: {
                                    status: 400,
                                    data: 'Point not in dataset bounds'
                                }
                            } as unknown as FetchBaseQueryError;
                        }
                        return fetchWithBQ(url);
                    })
                ).then((resList) => {
                    const responseWithItemsList = resList.map((rasterResponse, idx) => ({
                        rasterResponse,
                        itemData: rasterPointUrlDataList[idx].itemData,
                        coordinates: rasterPointUrlDataList[idx].coordinates
                    }));

                    return responseWithItemsList.map(({ rasterResponse, itemData, coordinates }) => {
                        if ('error' in rasterResponse && rasterResponse.error)
                            return { error: rasterResponse.error as unknown as FetchBaseQueryError };
                        const cogData = rasterResponse.data as TiTilerResponse;

                        return { data: formatRasterPointFeature(itemData, cogData, coordinates) };
                    });
                });
                return { data: responses };
            }
        })
    })
});

/**
Testing notes: Targets API Slice

* Any new endpoints MUST be added to makeTargetsApiSlice in packages/app/test/unit/index.tsx
Omitting endpoints there will cause tests for the component that uses them to fail 
(generally, they'll fail to run at all).
*/
export const targetAPISlice = createApi({
    reducerPath: 'target',
    baseQuery: fetchCustomBaseQuery({
        baseUrl: TARGETS_URL
    }),
    endpoints: (builder) => ({
        // get the data for a single target, currently limited to receiving basin and country as those
        // are the only fields we want for the moment
        getSingleTargetData: builder.query<TargetData, { targetId: string }>({
            query: ({ targetId }) => {
                return { url: `/targets?id=eq.${targetId}&select=basin,country` };
            },
            transformResponse: (response: TargetData[]) => response[0]
        })
    })
});

/**
Testing notes: Ogi API Slice

* Any new endpoints MUST be added to makeOgiApiSlice in packages/app/test/unit/index.tsx
Omitting endpoints there will cause tests for the component that uses them to fail 
(generally, they'll fail to run at all).
*/
export const ogiAPISlice = createApi({
    reducerPath: 'ogi',
    baseQuery: fetchCustomBaseQuery({ baseUrl: INFRASTRUCTURE_API_BASE_URL }),
    endpoints: (builder) => ({
        getOgiMetadata: builder.query<CombinedOGIMProperties[], { ogimIds: (string | number)[] }>({
            query: ({ ogimIds }) => {
                const queryString = `ogim_id=${encodeURI(`{${ogimIds}}`)}`;
                return { url: `/feature_metadata?${queryString}` };
            }
        }),
        getOgiNearPoint: builder.query({
            query: ({ longitude, latitude }: { longitude: number; latitude: number }) => {
                return {
                    url: `/features_within_radius?lng_center=${longitude}&lat_center=${latitude}&radius_m=${OGI_NEAR_PLUME_RADIUS_M}`
                };
            }
        }),
        getOgiDatabaseInfo: builder.query<LoadedData[], void>({
            query: () => {
                return { url: `/database_current_state` };
            }
        }),
        getOperatorsInBbox: builder.query({
            query: ({ bbox, flooredZoom }) => {
                const [west, south, east, north] = bbox;

                return {
                    url: `/visible_bounded_operator_list?xmin=${west}&ymin=${south}&xmax=${east}&ymax=${north}&z=${flooredZoom}&order=operator_name`
                };
            }
        })
    })
});

export const {
    useGetStacSearchQuery,
    useGetStacSearchByUrlQuery,
    useGetAllStacItemsForCollectionQuery,
    useGetHistogramDataQuery,
    useGetStacItemQuery,
    useGetRasterPointFeatureQuery,
    useGetRasterPointFeatureBySearchQuery,
    useGetMultipleStacSearchesByUrlQuery,
    useGetMultipleRasterPointsByStacSearchUrlsQuery
} = stacAPISlice;

export const { useGetSingleTargetDataQuery } = targetAPISlice;

export const {
    useGetOgiDatabaseInfoQuery,
    useGetOgiMetadataQuery,
    useGetOgiNearPointQuery,
    useGetOperatorsInBboxQuery
} = ogiAPISlice;
